import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  getCostObjectApprovalPendingBySupervisor,
  getCostObjectApprovalPendingByUser,
  getCostObjectApprovals,
  getCostObjectRequestForAccount,
  getDormantKeyNotifications,
} from '@/api/ParleyApi';
import { CostObjectApprovalRecord, DormantKeyNotification } from '@/types/parley';
import { useAuthStore } from '@/stores/authStore';
import {
  approvalNotificationId,
  dormantKeyNotificationId,
  loadReadNotificationIds,
  notificationKindOf,
  saveReadNotificationIds,
  type NotificationKind,
} from '@/common/composables/useNotificationReadState';

export type UserPendingApprovals = Record<string, CostObjectApprovalRecord[]>;

export const useNotificationStore = defineStore('notification', () => {
  const authStore = useAuthStore();
  const supervisorPendingApprovals = ref<CostObjectApprovalRecord[]>([]);
  const allPendingApprovals = ref<UserPendingApprovals>({});
  const userPendingApprovals = ref<UserPendingApprovals>({});
  const usersPendingApprovals = ref<UserPendingApprovals>({});
  const dormantKeyPausedKeys = ref<DormantKeyNotification[]>([]);
  const loadingNotifications = ref(false);

  // ── Read state for the header bell ──
  // The API returns every notification whose condition still holds, so the
  // bell would otherwise show a paused key until someone unpaused it. Ids a
  // user has viewed or marked read are kept per kerberos (device-local, see
  // useNotificationReadState.ts) and hidden from the bell. Pruned to the
  // occurrences the API still reports, one kind at a time, so a set never
  // grows past what is currently notifiable and marking the approvals read
  // never touches the dormant-key entries loaded by the other fetch.
  const readNotificationIds = ref<Set<string>>(new Set());

  const kerberos = () => authStore.kerberos ?? '';

  const reconcileReadIds = (kind: NotificationKind, currentIds: string[]) => {
    const current = new Set(currentIds);
    const next = new Set(
      [...loadReadNotificationIds(kerberos())].filter(
        (id) => notificationKindOf(id) !== kind || current.has(id)
      )
    );
    readNotificationIds.value = next;
    saveReadNotificationIds(kerberos(), next);
  };

  const unreadSupervisorApprovals = computed(() =>
    supervisorPendingApprovals.value.filter(
      (n) => !readNotificationIds.value.has(approvalNotificationId(n))
    )
  );
  const unreadDormantKeyNotifications = computed(() =>
    dormantKeyPausedKeys.value.filter(
      (n) => !readNotificationIds.value.has(dormantKeyNotificationId(n))
    )
  );
  const unreadNotificationCount = computed(
    () => unreadSupervisorApprovals.value.length + unreadDormantKeyNotifications.value.length
  );

  const markNotificationsRead = (ids: string[]): void => {
    const next = new Set(readNotificationIds.value);
    ids.forEach((id) => next.add(id));
    readNotificationIds.value = next;
    saveReadNotificationIds(kerberos(), next);
  };

  const markAllNotificationsRead = (): void => {
    markNotificationsRead([
      ...supervisorPendingApprovals.value.map(approvalNotificationId),
      ...dormantKeyPausedKeys.value.map(dormantKeyNotificationId),
    ]);
  };

  const getPendingApprovals = async (): Promise<void> => {
    if (!authStore.isSupport && !authStore.isSuperAdmin) {
      return;
    }

    try {
      loadingNotifications.value = true;
      const res = await getCostObjectApprovals('pending');
      if (res.data) {
        allPendingApprovals.value = res.data?.requests?.reduce(
          (acc: Record<string, CostObjectApprovalRecord[]>, curr: CostObjectApprovalRecord) => {
            const key = curr.accountId;
            acc[key] ??= [];
            acc[key].push(curr);
            return acc;
          },
          {}
        );
      }
    } catch (error: unknown) {
      console.log(`ERROR getting approvals: ${JSON.stringify(error)}`);
    } finally {
      loadingNotifications.value = false;
    }
  };

  const getSupervisorPendingApprovals = async (): Promise<void> => {
    try {
      loadingNotifications.value = true;
      const res = await getCostObjectApprovalPendingBySupervisor();
      if (res.data) {
        supervisorPendingApprovals.value = res.data?.requests ?? [];
      }
      reconcileReadIds('approval', supervisorPendingApprovals.value.map(approvalNotificationId));
    } catch (error: unknown) {
      console.log(`ERROR getting approvals: ${JSON.stringify(error)}`);
    } finally {
      loadingNotifications.value = false;
    }
  };

  const getCurrentUserPendingApprovals = async (): Promise<void> => {
    try {
      loadingNotifications.value = true;
      const res = await getCostObjectApprovalPendingByUser();
      if (res.data) {
        userPendingApprovals.value = res.data?.requests?.reduce(
          (acc: Record<string, CostObjectApprovalRecord[]>, curr: CostObjectApprovalRecord) => {
            const key = curr.accountId;
            acc[key] ??= [];
            acc[key].push(curr);
            return acc;
          },
          {}
        );
      }
    } catch (error: unknown) {
      console.log(`ERROR getting user approvals: ${JSON.stringify(error)}`);
    } finally {
      loadingNotifications.value = false;
    }
  };

  const getSelectedUserPendingApprovals = async (
    accountId: string,
    email?: string
  ): Promise<void> => {
    //TODO: For now we use the fist index of the approvals because we only have account cost object approvals. This will have to change when more types are added.
    if (usersPendingApprovals.value[accountId]) return;
    try {
      loadingNotifications.value = true;
      const res = await getCostObjectApprovalPendingByUser(email);
      if (res.data) {
        usersPendingApprovals.value = res.data?.requests?.reduce(
          (acc: Record<string, CostObjectApprovalRecord[]>, curr: CostObjectApprovalRecord) => {
            const key = curr.accountId;
            acc[key] ??= [];
            acc[key].push(curr);
            return acc;
          },
          {}
        );
      }
    } catch (error: unknown) {
      console.log(`ERROR getting user approvals: ${JSON.stringify(error)}`);
    } finally {
      loadingNotifications.value = false;
    }
  };

  // Fetches the cost-object-request for one account's *current* cost object,
  // if any. Backed by a direct GetItem (not the GSI1 "pending" index the
  // functions above use), so it's safe to call right after a mutation that
  // just wrote the record without racing GSI replication lag. Unlike
  // getSelectedUserPendingApprovals, always refetches — no permanent cache.
  const getAccountPendingRequest = async (accountId: string): Promise<void> => {
    try {
      loadingNotifications.value = true;
      const res = await getCostObjectRequestForAccount(accountId);
      usersPendingApprovals.value = {
        ...usersPendingApprovals.value,
        [accountId]: res.data?.requests ?? [],
      };
    } catch (error: unknown) {
      console.log(`ERROR getting account cost-object request: ${JSON.stringify(error)}`);
    } finally {
      loadingNotifications.value = false;
    }
  };

  // Dormant-key auto-pause notifications (warning + already-paused) for
  // every account the caller owns or administers. No role gating, unlike
  // getPendingApprovals — targets any account owner/admin, not a staff role.
  const getDormantKeyPausedKeys = async (): Promise<void> => {
    try {
      loadingNotifications.value = true;
      const res = await getDormantKeyNotifications();
      dormantKeyPausedKeys.value = res.data?.notifications ?? [];
      reconcileReadIds('dormant', dormantKeyPausedKeys.value.map(dormantKeyNotificationId));
    } catch (error: unknown) {
      console.log(`ERROR getting dormant key notifications: ${JSON.stringify(error)}`);
    } finally {
      loadingNotifications.value = false;
    }
  };

  return {
    supervisorPendingApprovals,
    userPendingApprovals,
    usersPendingApprovals,
    allPendingApprovals,
    dormantKeyPausedKeys,
    readNotificationIds,
    unreadSupervisorApprovals,
    unreadDormantKeyNotifications,
    unreadNotificationCount,
    markNotificationsRead,
    markAllNotificationsRead,
    getPendingApprovals,
    getSupervisorPendingApprovals,
    getCurrentUserPendingApprovals,
    getSelectedUserPendingApprovals,
    getAccountPendingRequest,
    getDormantKeyPausedKeys,
    loadingNotifications,
  };
});
